/**
 * Phase 19: the app's home directory, kept in IndexedDB.
 *
 * The module sees an iOS-like home at `/data` (`HOME`): `Documents`,
 * `Library/Application Support` (`XDG_DATA_HOME`) and `Library/Caches`
 * (`XDG_CACHE_HOME`), so `URL.documentsDirectory`, `FileManager.urls(for:in:)`
 * and `Data.write(to:)` work as on a phone; `/tmp` is a second mount that
 * nothing keeps. The page loads the home from the app's database before the
 * module starts, and after any event (or executor run) that wrote to a file
 * it compares the tree with what it last saved and writes the difference.
 * One database per app, `sb-files:<App>`, with one object store, `entries`,
 * keyed by `path` (relative to the home, `Documents/notes.txt`); a row is
 * `{path, data}` for a file and `{path, directory: true}` for a directory.
 * See docs/ops-protocol.md, "Phase 19".
 */

import { Directory, File, type Inode } from '@bjorn3/browser_wasi_shim';

/** Where the home is mounted, and the environment that points Foundation at it. */
export const HOME = '/data';
export const TMP = '/tmp';
export const HOME_ENVIRONMENT: readonly string[] = [
  `HOME=${HOME}`,
  `XDG_DATA_HOME=${HOME}/Library/Application Support`,
  `XDG_CACHE_HOME=${HOME}/Library/Caches`,
  `TMPDIR=${TMP}`,
];

/** The directories every home has, as on iOS. */
export const HOME_DIRECTORIES: readonly string[] = ['Documents', 'Library', 'Library/Application Support', 'Library/Caches'];

/** A home's contents: path → bytes for a file, `null` for a directory. */
export type HomeEntries = Map<string, Uint8Array | null>;

/** A writable WASI directory tree from saved entries, with the standard directories. */
export function homeDirectory(entries: HomeEntries): Directory {
  const root = new Directory(new Map<string, Inode>());
  const directoryAt = (parts: readonly string[]): Directory => {
    let dir = root;
    for (const part of parts) {
      const existing = dir.contents.get(part);
      if (existing instanceof Directory) {
        dir = existing;
        continue;
      }
      const next = new Directory(new Map<string, Inode>());
      dir.contents.set(part, next);
      dir = next;
    }
    return dir;
  };
  for (const path of HOME_DIRECTORIES) directoryAt(path.split('/'));
  // Directories before files, shallow paths first, so a parent always exists.
  const sorted = [...entries].sort(([a, aData], [b, bData]) => Number(aData !== null) - Number(bData !== null) || a.split('/').length - b.split('/').length);
  for (const [path, data] of sorted) {
    const parts = path.split('/').filter((part) => part !== '' && part !== '.' && part !== '..');
    if (parts.length === 0) continue;
    if (data === null) {
      directoryAt(parts);
    } else {
      directoryAt(parts.slice(0, -1)).contents.set(parts[parts.length - 1]!, new File(data));
    }
  }
  return root;
}

/** Every file and directory under `root`, by path (the live bytes of each file). */
export function homeEntries(root: Directory): HomeEntries {
  const entries: HomeEntries = new Map();
  const walk = (dir: Directory, prefix: string): void => {
    for (const [name, child] of dir.contents) {
      const path = prefix === '' ? name : `${prefix}/${name}`;
      if (child instanceof Directory) {
        entries.set(path, null);
        walk(child, path);
      } else if (child instanceof File) {
        entries.set(path, child.data);
      }
    }
  };
  walk(root, '');
  return entries;
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** What changed from `saved` to `current`: entries to write (copied) and paths to delete. */
export interface HomeChanges {
  put: [string, Uint8Array | null][];
  remove: string[];
}

export function diffHome(saved: HomeEntries, current: HomeEntries): HomeChanges {
  const put: [string, Uint8Array | null][] = [];
  const remove: string[] = [];
  for (const [path, data] of current) {
    const before = saved.get(path);
    if (before === undefined) {
      put.push([path, data === null ? null : data.slice()]);
    } else if (data === null ? before !== null : before === null || !sameBytes(before, data)) {
      put.push([path, data === null ? null : data.slice()]);
    }
  }
  for (const path of saved.keys()) if (!current.has(path)) remove.push(path);
  return { put, remove };
}

/** `saved` with `changes` applied (what was just written). */
export function applyHomeChanges(saved: HomeEntries, changes: HomeChanges): HomeEntries {
  const next: HomeEntries = new Map(saved);
  for (const path of changes.remove) next.delete(path);
  for (const [path, data] of changes.put) next.set(path, data);
  return next;
}

/** Where an app's home lives between launches. */
export interface FilesPersistence {
  load(): Promise<HomeEntries>;
  /** Writes one set of changes; writes happen in the order they were made. */
  apply(changes: HomeChanges): Promise<void>;
  clear(): Promise<void>;
}

/**
 * A row of the `entries` store: a file's bytes or a directory, with when it
 * was last written where the writer said. A React app's `fileManager`
 * (files.ts) may keep a file as the `Blob` the viewer handed over, which
 * IndexedDB stores without reading; the module's loader reads one on load.
 */
export interface EntryRow {
  path: string;
  data?: Uint8Array | Blob;
  directory?: boolean;
  /** Milliseconds since the epoch; rows the module wrote (and older rows) have none. */
  modified?: number;
  /** When it was made, in milliseconds since the epoch, where the writer said. */
  created?: number;
}

export const ENTRIES = 'entries';

export function filesDatabaseName(app: string): string {
  return `sb-files:${app}`;
}

/** The app's files database, opened (and made) once per `app`; `factory` is the browser's unless a test passes one. */
export function openFilesDatabase(app: string, factory: IDBFactory = indexedDB): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = factory.open(filesDatabaseName(app), 1);
    open.onupgradeneeded = () => {
      if (!open.result.objectStoreNames.contains(ENTRIES)) open.result.createObjectStore(ENTRIES, { keyPath: 'path' });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error(`could not open ${filesDatabaseName(app)}`));
    open.onblocked = () => reject(new Error(`${filesDatabaseName(app)} is blocked by another tab`));
  });
}

export function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

export function done(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
  });
}

/** The app's home in IndexedDB (`factory` is the browser's unless a test passes one). */
export function indexedDBFiles(app: string, factory: IDBFactory = indexedDB): FilesPersistence {
  let opened: Promise<IDBDatabase> | null = null;
  const database = (): Promise<IDBDatabase> => (opened ??= openFilesDatabase(app, factory));
  let queue: Promise<void> = Promise.resolve();

  return {
    async load() {
      const db = await database();
      const rows = await request(db.transaction(ENTRIES, 'readonly').objectStore(ENTRIES).getAll() as IDBRequest<EntryRow[]>);
      const entries: HomeEntries = new Map();
      for (const row of rows) {
        if (row.directory) entries.set(row.path, null);
        else if (row.data !== undefined && typeof Blob !== 'undefined' && row.data instanceof Blob) entries.set(row.path, new Uint8Array(await row.data.arrayBuffer()));
        else entries.set(row.path, new Uint8Array((row.data as Uint8Array | undefined) ?? new Uint8Array()));
      }
      return entries;
    },
    apply(changes) {
      queue = queue.then(async () => {
        const db = await database();
        const transaction = db.transaction(ENTRIES, 'readwrite');
        const store = transaction.objectStore(ENTRIES);
        for (const path of changes.remove) store.delete(path);
        for (const [path, data] of changes.put) {
          const row: EntryRow = data === null ? { path, directory: true } : { path, data };
          store.put(row);
        }
        await done(transaction);
      });
      return queue;
    },
    async clear() {
      const db = await database();
      const transaction = db.transaction(ENTRIES, 'readwrite');
      transaction.objectStore(ENTRIES).clear();
      await done(transaction);
    },
  };
}
